/**
 * Level meters, spectrum and spectrogram from the mixer's analysers
 * (design §7.4).
 *
 * @module
 */
import { useContext, useEffect, useRef } from "react";
import { useTheme } from "@mui/material/styles";
import { bands, heat, levels, PeakHold } from "../audio/meters";
import { UiFramesContext } from "./uiFrames";

const METER_MIN_DB = -60;
const SPECTRUM_MIN_DB = -100;
const SPECTRUM_MAX_DB = -20;
const SPECTRUM_BANDS = 32;
/** Outside a player, which draws them with its own updates. */
const DRAW_EVERY_MS = 100;
/** Scroll speed: CSS pixels per second. */
const SPECTROGRAM_PX_PER_S = 40;
const SPECTROGRAM_MIN_DB = -100;
const SPECTROGRAM_MAX_DB = -40;

type Draw = (
  g: CanvasRenderingContext2D,
  el: HTMLCanvasElement,
  now: number,
  dt: number,
) => void;

/**
 * A canvas redrawn with the player's other updates (or on its own timer
 * outside one), sized for the display's pixel ratio; `makeDraw` runs once per
 * change of `deps`, so its buffers aren't reallocated on each render. With
 * `clear` false, each frame draws over the last.
 */
function useCanvasLoop(makeDraw: () => Draw, deps: unknown[], clear = true) {
  const ref = useRef<HTMLCanvasElement>(null);
  const frames = useContext(UiFramesContext);
  useEffect(() => {
    const el = ref.current;
    const g = el?.getContext("2d");
    if (!el || !g) return;
    const ratio = window.devicePixelRatio || 1;
    el.width = Math.round(el.clientWidth * ratio);
    el.height = Math.round(el.clientHeight * ratio);
    const draw = makeDraw();
    const step = (now: number, dt: number) => {
      if (clear) g.clearRect(0, 0, el.width, el.height);
      draw(g, el, now, dt);
    };
    if (frames) return frames.add(step);
    let frame = 0;
    let last = performance.now();
    const loop = (now: number) => {
      frame = requestAnimationFrame(loop);
      if (now - last < DRAW_EVERY_MS) return;
      step(now, now - last);
      last = now;
    };
    frame = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the caller lists what makeDraw depends on
  }, [frames, ...deps]);
  return ref;
}

const fraction = (db: number, min: number, max: number) =>
  Math.min(1, Math.max(0, (db - min) / (max - min)));

/** A source's level: RMS as a bar, peak as a held line (-60 to 0 dBFS). */
export function LevelMeter({
  analyser,
  name,
}: {
  analyser: AnalyserNode;
  name: string;
}) {
  const theme = useTheme();
  const canvas = useCanvasLoop(() => {
    const samples = new Float32Array(analyser.fftSize);
    const hold = new PeakHold();
    return (g, el, now, dt) => {
      const { width: w, height: h } = el;
      analyser.getFloatTimeDomainData(samples);
      const { peak, rms } = levels(samples);
      const [held = peak] = hold.update([peak], now, dt);
      g.fillStyle = theme.palette.action.hover;
      g.fillRect(0, 0, w, h);
      g.fillStyle =
        peak > -1 ? theme.palette.error.main : theme.palette.success.main;
      g.fillRect(0, 0, w * fraction(rms, METER_MIN_DB, 0), h);
      g.fillStyle = theme.palette.text.primary;
      g.fillRect(Math.max(0, w * fraction(held, METER_MIN_DB, 0) - 2), 0, 2, h);
      el.setAttribute(
        "aria-valuenow",
        String(Math.round(Math.max(rms, METER_MIN_DB))),
      );
    };
  }, [analyser, theme]);
  return (
    <canvas
      ref={canvas}
      role="meter"
      aria-label={`Level ${name}`}
      aria-valuemin={METER_MIN_DB}
      aria-valuemax={0}
      style={{ width: 80, height: 8, borderRadius: 2 }}
    />
  );
}

/** The mix's spectrum: log-frequency bars, 40 Hz to 20 kHz, with held peaks. */
export function Spectrum({ analyser }: { analyser: AnalyserNode }) {
  const theme = useTheme();
  const canvas = useCanvasLoop(() => {
    const spectrum = new Float32Array(analyser.frequencyBinCount);
    const hold = new PeakHold(800, 30);
    return (g, el, now, dt) => {
      const { width: w, height: h } = el;
      analyser.getFloatFrequencyData(spectrum);
      const b = bands(spectrum, analyser.context.sampleRate, SPECTRUM_BANDS);
      const held = hold.update(b, now, dt);
      const bw = w / SPECTRUM_BANDS;
      b.forEach((db, i) => {
        const bh = h * fraction(db, SPECTRUM_MIN_DB, SPECTRUM_MAX_DB);
        g.fillStyle = theme.palette.primary.main;
        g.fillRect(i * bw + 1, h - bh, bw - 2, bh);
        const ph =
          h * fraction(held[i] ?? db, SPECTRUM_MIN_DB, SPECTRUM_MAX_DB);
        g.fillStyle = theme.palette.text.secondary;
        g.fillRect(i * bw + 1, h - ph - 1, bw - 2, 1);
      });
    };
  }, [analyser, theme]);
  return (
    <canvas
      ref={canvas}
      role="img"
      aria-label="Spectrum of the mix"
      style={{ width: 160, height: 32 }}
    />
  );
}

/**
 * The mix's spectrogram, scrolling right to left: log frequency (40 Hz to
 * 20 kHz) up, level as colour. It stands still while playback is paused.
 */
export function Spectrogram({
  analyser,
  playing,
}: {
  analyser: AnalyserNode;
  playing: boolean;
}) {
  const running = useRef(playing);
  useEffect(() => {
    running.current = playing;
  }, [playing]);
  const canvas = useCanvasLoop(
    () => {
      const spectrum = new Float32Array(analyser.frequencyBinCount);
      let column: ImageData | null = null;
      let started = false;
      return (g, el, _now, dt) => {
        const { width: w, height: h } = el;
        if (!started) {
          g.fillStyle = `rgb(${heat(0).join(",")})`;
          g.fillRect(0, 0, w, h);
          started = true;
        }
        if (!running.current) return;
        const cw = Math.max(
          1,
          Math.min(
            w,
            Math.round(
              (SPECTROGRAM_PX_PER_S * (window.devicePixelRatio || 1) * dt) /
                1000,
            ),
          ),
        );
        if (column?.width !== cw) column = g.createImageData(cw, h);
        analyser.getFloatFrequencyData(spectrum);
        const b = bands(spectrum, analyser.context.sampleRate, h);
        for (let y = 0; y < h; y++) {
          const rgb = heat(
            fraction(
              b[h - 1 - y] ?? -Infinity,
              SPECTROGRAM_MIN_DB,
              SPECTROGRAM_MAX_DB,
            ),
          );
          for (let x = 0; x < cw; x++) {
            const at = (y * cw + x) * 4;
            column.data.set(rgb, at);
            column.data[at + 3] = 255;
          }
        }
        g.drawImage(el, -cw, 0);
        g.putImageData(column, w - cw, 0);
      };
    },
    [analyser],
    false,
  );
  return (
    <canvas
      ref={canvas}
      role="img"
      aria-label="Spectrogram of the mix"
      style={{ width: "100%", height: 96, display: "block", borderRadius: 4 }}
    />
  );
}
